// Interactive React Flow editor (§8) — the net-new editing layer on top of the read-only viewer.
//
// Loads a flow's DSL from the Design Model, renders it editable (add/move/delete nodes, edit
// label/props, add/rewire/delete edges), validates against the block catalog, and saves the DSL
// back via the optimistic-concurrency PATCH (§4.5) when the engineer clicks SAVE — edits are NOT
// autosaved, so unsaved edits are guarded: `beforeunload` blocks a reload/close and the Back button
// confirms before discarding (P0.5). A stale-version 409 on save offers "Reload & retry" that
// re-reads the project version and re-submits the LOCAL graph (the edits are never thrown away).
// Downstream, both Lucid sync and the Build-phase importable-JSON synthesis consume this same
// edited DSL ("edit once, get correct Lucid + import").
//
// Edge presentation mirrors the AWS Export viewer (diagrams/): each block exposes ONE labelled source
// handle PER BRANCH (success/condition/error), lines are coloured + dashed by branch type with the
// accurate exit condition pinned at the exit, and every branch is independently connect/reroute/delete-
// able. The branch model + label/colour derivation lives in flowEdges.js (keyed off the DSL).

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Handle, Position,
  addEdge, useNodesState, useEdgesState, MarkerType, reconnectEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box, Group, Button, Stack, Text, Title, TextInput, Textarea, Loader, Center, Alert, Badge,
  ScrollArea, Divider, ActionIcon, Menu, Select, Modal,
} from '@mantine/core';
import { IconArrowLeft, IconDeviceFloppy, IconTrash, IconPlus, IconAlertCircle, IconAlertTriangle, IconLayoutGrid, IconArrowsSplit2 } from '@tabler/icons-react';

import { useAuth } from '../../../shared/contexts/AuthContext';
import { getProject, getDesignModel, saveFlowDsl } from './api_scripts/solnStudioClient';
import {
  paletteFor, branches, isTerminal, validateGraph, nodeMeta, friendlyLabel, CATEGORY_COLOR,
  deriveProps, nodeDescription, nodeDetail,
  resolveFlowType, isModuleFlowType, FLOW_TYPE_SELECT_DATA,
} from './flowCatalog';
import {
  branchModel, branchKey, edgeLabel, edgeColorKind, styleForEdge, edgeDataForBranch, EDGE_KIND_STYLE,
} from './flowEdges';
import { layoutNodes, needsLayout } from './utils/layoutFlow';

let _seq = 0;
const newNodeId = (type) => `n_${type}_${Date.now()}_${_seq++}`;
const newEdgeId = () => `e_${Date.now()}_${_seq++}`;

// React Flow line props (type/style/marker) for an edge given its semantic data ({ kind, condition,
// errorType }). Colour + dash come from flowEdges.styleForEdge so the viewer matches the Export tool.
function rfEdgeStyle(data) {
  const { color, dash } = styleForEdge(data);
  return {
    type: 'smoothstep',
    style: { stroke: color, strokeWidth: 2, strokeDasharray: dash },
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
  };
}

// Custom node: category-colored accent card showing icon + friendly label, a faint purpose line, a
// compact key-params block (what the block actually does), and the type chip. Distinct accent for the
// start node. Each branch (success/condition/error) gets its OWN bottom source handle, distributed
// across the card edge with the accurate exit label pinned at the handle (mirrors the Export viewer's
// labelled handles) — so lines leave from separate, easily-connectable points.
function FlowNode({ data, selected }) {
  const meta = nodeMeta(data.type);
  const accent = data.isStart ? '#2f9e44' : (CATEGORY_COLOR[meta.category] || CATEGORY_COLOR.general);
  const detail = data.detail || [];
  const branchList = data.branches || [];
  return (
    <div
      style={{
        position: 'relative',
        width: 228, minHeight: 48, background: '#fff',
        // Use border longhands (not the `border` shorthand) so the `selected` rerender doesn't mix
        // shorthand + `borderLeft` longhand — React warns about that and it can cause styling bugs.
        borderTop: `1px solid ${selected ? accent : '#ced4da'}`,
        borderRight: `1px solid ${selected ? accent : '#ced4da'}`,
        borderBottom: `1px solid ${selected ? accent : '#ced4da'}`,
        borderLeft: `5px solid ${accent}`,
        borderRadius: 8, padding: '8px 10px',
        boxShadow: selected ? `0 0 0 2px ${accent}33` : '0 1px 2px rgba(0,0,0,0.08)',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: accent }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 14, lineHeight: 1 }}>{meta.icon}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#212529', wordBreak: 'break-word', flex: 1 }}>
          {data.label}
        </span>
      </div>
      {data.description && (
        <div style={{ marginTop: 3, fontSize: 9.5, color: '#868e96', lineHeight: 1.25 }}>{data.description}</div>
      )}
      {detail.length > 0 && (
        <div style={{ marginTop: 5, padding: '4px 6px', background: '#f8f9fa', borderRadius: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {detail.map((d, i) => (
            <div key={i} style={{ fontSize: 9.5, lineHeight: 1.3, display: 'flex', gap: 4 }}>
              <span style={{ color: '#868e96', fontWeight: 600, flexShrink: 0 }}>{d.label}:</span>
              <span style={{ color: '#343a40', wordBreak: 'break-word' }}>{d.value}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
        {data.isStart && <span style={{ fontSize: 9, fontWeight: 700, color: '#2f9e44' }}>START</span>}
        <span style={{ fontSize: 9, color: '#868e96' }}>{data.type}</span>
      </div>
      {branchList.map((b, i) => <BranchHandle key={b.key} branch={b} index={i} total={branchList.length} />)}
    </div>
  );
}

// One labelled source handle for a branch, spread along the node's bottom edge and coloured by kind,
// with the accurate exit label pinned just below it (rotated, like the Export viewer's createLabeledHandle).
function BranchHandle({ branch, index, total }) {
  const color = (EDGE_KIND_STYLE[branch.kind] || EDGE_KIND_STYLE.success).color;
  const leftPct = (100 / (total + 1)) * (index + 1);
  return (
    <>
      <Handle
        type="source"
        id={branch.key}
        position={Position.Bottom}
        style={{
          left: `${leftPct}%`, bottom: -5, transform: 'translateX(-50%)',
          width: 9, height: 9, background: color, border: '1px solid #fff', zIndex: 5,
        }}
      />
      {branch.label ? (
        <div
          style={{
            position: 'absolute', left: `${leftPct}%`, top: '100%', marginTop: 8,
            transform: 'translateX(-50%) rotate(-45deg)', transformOrigin: 'top center',
            fontSize: 9, whiteSpace: 'nowrap', background: '#fff', padding: '1px 4px',
            borderRadius: 3, border: `1px solid ${color}`, color, fontWeight: 600,
            pointerEvents: 'none', zIndex: 4,
          }}
        >
          {branch.label}
        </div>
      ) : null}
    </>
  );
}

const NODE_TYPES = { flowNode: FlowNode };

function dslToReactFlow(dsl) {
  const start = dsl.start;
  const dslEdges = dsl.edges || [];
  const byFrom = new Map();
  for (const e of dslEdges) {
    if (!byFrom.has(e.from)) byFrom.set(e.from, []);
    byFrom.get(e.from).push(e);
  }
  const nodes = (dsl.nodes || []).map((n) => {
    const label = (n.label && n.label !== n.type) ? n.label : friendlyLabel(n);
    // Generated flows carry only raw `parameters` (no `props`). Derive the friendly accessors so the
    // node card + side panel show real detail. `parameters` stays authoritative — derivation is
    // display-only and never persisted (reactFlowToDsl writes back data.props verbatim, but generated
    // nodes are saved from data.parameters which is preserved untouched).
    const props = (n.props && Object.keys(n.props).length) ? n.props : deriveProps(n);
    return {
      id: n.id,
      position: n.position || { x: 0, y: 0 },
      data: {
        label, type: n.type, isStart: n.id === start,
        props, parameters: n.parameters || {}, meta: n.meta || {},
        description: nodeDescription(n.type),
        detail: nodeDetail(n),
        branches: branchModel(n.type, byFrom.get(n.id) || []),
      },
      type: 'flowNode',
    };
  });
  const edges = dslEdges.map((e, i) => {
    const data = { kind: e.kind || 'default', condition: e.condition, errorType: e.errorType, branchKey: branchKey(e) };
    return {
      id: e.id || `e_${i}`,
      source: e.from,
      target: e.to,
      sourceHandle: data.branchKey,
      ...rfEdgeStyle(data),
      data,
    };
  });
  return { nodes, edges };
}

function reactFlowToDsl(base, nodes, edges, start) {
  return {
    ...base,
    start: start || base.start || (nodes[0] && nodes[0].id),
    nodes: nodes.map((n) => ({
      id: n.id, type: n.data.type, label: n.data.label, position: n.position,
      props: n.data.props || {}, parameters: n.data.parameters || {}, meta: n.data.meta || {},
    })),
    // Persist only the semantic edge fields — the per-branch handle id + line styling are recomputed on
    // load from kind/condition/errorType (branchKey), so the DSL stays clean + lossless.
    edges: edges.map((e) => ({
      id: e.id, from: e.source, to: e.target, kind: e.data?.kind || 'default',
      condition: e.data?.condition, errorType: e.data?.errorType,
    })),
  };
}

function DiagramEditorInner() {
  const { id, flowId } = useParams();
  const navigate = useNavigate();
  const { getApiToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [flowMeta, setFlowMeta] = useState(null); // { name, type, dslBase, start }
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selected, setSelected] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [conflict, setConflict] = useState(false); // stale-version 409 on save → offer reload & retry
  const [leaveConfirm, setLeaveConfirm] = useState(false); // Back pressed with unsaved edits
  const autoSavedRef = useRef(false); // guard: auto-layout save happens at most once per mount

  // Unsaved-edit guard (P0.5): edits are NOT autosaved — block a reload/close while dirty. (In-app
  // navigation goes through the Back button below, which confirms via the leaveConfirm modal.)
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // required by Chrome to show the native prompt
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const goBack = useCallback(() => navigate(`../${id}`), [navigate, id]);
  const requestBack = useCallback(() => {
    if (dirty) setLeaveConfirm(true);
    else goBack();
  }, [dirty, goBack]);

  useEffect(() => {
    (async () => {
      try {
        const proj = await getProject(id, getApiToken);
        setProject(proj);
        const { designModel } = await getDesignModel(id, getApiToken);
        const flow = (designModel.flows || []).find((f) => f.id === flowId);
        if (!flow) throw new Error('Flow not found in the Design Model.');
        const base = flow.dsl || { kind: 'contact_flow' };
        const start = flow.dsl?.start;
        const { nodes: n, edges: e } = dslToReactFlow(base);

        // Generated flows carry no node positions → auto-arrange so the diagram is readable.
        const didLayout = needsLayout(n);
        const laidOut = didLayout ? layoutNodes(n, e) : n;
        setNodes(laidOut);
        setEdges(e);
        setFlowMeta({ name: flow.name, type: flow.type, flowType: resolveFlowType(flow), dslBase: base, start });
        setLoading(false);

        // Persist the computed layout once (layoutOnly → does NOT mark the flow human-edited, so a
        // later Refine can still regenerate it). A held generation lock is a no-op (layout still shows).
        if (didLayout && !autoSavedRef.current) {
          autoSavedRef.current = true;
          try {
            const dsl = reactFlowToDsl(base, laidOut, e, start);
            const res = await saveFlowDsl(id, flowId, proj.version, dsl, getApiToken, { layoutOnly: true });
            setProject((p) => ({ ...p, version: res.project.version }));
            setSaveMsg({ type: 'ok', text: 'Auto-arranged & saved.' });
          } catch (err) {
            if (err.reason === 'locked') setSaveMsg({ type: 'info', text: 'Auto-arranged (not saved — a generation job is running).' });
            else setSaveMsg({ type: 'info', text: 'Auto-arranged (save it to keep the layout).' });
          }
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    })();
  }, [id, flowId, getApiToken, setNodes, setEdges]);

  const markDirty = useCallback(() => { setDirty(true); setSaveMsg(null); }, []);

  // Keep each node's branch handles in sync with its current outgoing edges (an edit that adds/retypes a
  // branch should grow/relabel the node's connectors). Recomputes branchModel and only writes when the
  // handle set actually changes, so it can't loop.
  useEffect(() => {
    setNodes((nds) => {
      const byFrom = new Map();
      for (const e of edges) {
        if (!byFrom.has(e.source)) byFrom.set(e.source, []);
        byFrom.get(e.source).push(e.data || {});
      }
      let changed = false;
      const next = nds.map((n) => {
        const model = branchModel(n.data.type, byFrom.get(n.id) || []);
        const newKeys = model.map((b) => `${b.key}:${b.label}`).join('|');
        const oldKeys = (n.data.branches || []).map((b) => `${b.key}:${b.label}`).join('|');
        if (newKeys === oldKeys) return n;
        changed = true;
        return { ...n, data: { ...n.data, branches: model } };
      });
      return changed ? next : nds;
    });
  }, [edges, setNodes]);

  // Change the flow's Connect Type (contact flows only — a module's type is fixed). Saved with the DSL.
  const setFlowType = useCallback((ft) => {
    if (!ft) return;
    setFlowMeta((m) => (m ? { ...m, flowType: ft } : m));
    markDirty();
  }, [markDirty]);

  const tidyLayout = useCallback(() => {
    setNodes((nds) => layoutNodes(nds, edges));
    markDirty();
  }, [setNodes, edges, markDirty]);

  // Connect from a SPECIFIC branch handle → the new edge inherits that branch's semantics (a Press-1
  // condition, a Timeout error), not a blanket success. One target per branch: connecting a branch that
  // is already wired replaces it (drag-to-reroute).
  const onConnect = useCallback((conn) => {
    const handle = conn.sourceHandle || 'default';
    const srcNode = nodes.find((n) => n.id === conn.source);
    const branch = srcNode?.data?.branches?.find((b) => b.key === handle);
    const data = edgeDataForBranch(branch || { kind: 'default', key: handle });
    const newEdge = {
      id: newEdgeId(), source: conn.source, target: conn.target, sourceHandle: handle,
      ...rfEdgeStyle(data), data,
    };
    setEdges((eds) => addEdge(
      newEdge,
      eds.filter((e) => !(e.source === conn.source && (e.sourceHandle || 'default') === handle)),
    ));
    markDirty();
  }, [nodes, setEdges, markDirty]);

  // Drag an edge endpoint to a new node/handle (reroute) — keeps the edge id + semantics.
  const onReconnect = useCallback((oldEdge, newConn) => {
    setEdges((eds) => reconnectEdge(oldEdge, newConn, eds));
    markDirty();
  }, [setEdges, markDirty]);

  const addNode = (type, label) => {
    const node = {
      id: newNodeId(type),
      position: { x: 120 + Math.random() * 200, y: 80 + Math.random() * 200 },
      data: {
        label, type, isStart: false, props: {}, parameters: {}, meta: {},
        description: nodeDescription(type), detail: [], branches: branchModel(type, []),
      },
      type: 'flowNode',
    };
    setNodes((nds) => nds.concat(node));
    markDirty();
  };

  const updateSelectedNode = (patch) => {
    setNodes((nds) => nds.map((n) => (n.id === selected?.id ? { ...n, data: { ...n.data, ...patch } } : n)));
    setSelected((s) => (s ? { ...s, data: { ...s.data, ...patch } } : s));
    markDirty();
  };

  const deleteSelected = () => {
    if (!selected) return;
    setNodes((nds) => nds.filter((n) => n.id !== selected.id));
    setEdges((eds) => eds.filter((e) => e.source !== selected.id && e.target !== selected.id));
    setSelected(null);
    markDirty();
  };

  // Edit the selected edge's semantics (type/condition value/error type). Recomputes its handle
  // binding + line styling so the colour/label/connector update live.
  const updateSelectedEdge = (patchData) => {
    if (!selectedEdge) return;
    setEdges((eds) => eds.map((e) => {
      if (e.id !== selectedEdge.id) return e;
      const data = { ...e.data, ...patchData };
      data.branchKey = branchKey(data);
      return { ...e, sourceHandle: data.branchKey, ...rfEdgeStyle(data), data };
    }));
    setSelectedEdge((se) => (se ? { ...se, data: { ...se.data, ...patchData } } : se));
    markDirty();
  };

  const deleteSelectedEdge = () => {
    if (!selectedEdge) return;
    setEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id));
    setSelectedEdge(null);
    markDirty();
  };

  const currentDsl = useMemo(() => {
    if (!flowMeta) return null;
    const dsl = reactFlowToDsl(flowMeta.dslBase, nodes, edges, flowMeta.start);
    // Persist the engineer's chosen flow type (the type Select edits flowMeta.flowType).
    if (flowMeta.flowType) dsl.flowType = flowMeta.flowType;
    return dsl;
  }, [flowMeta, nodes, edges]);
  const validation = useMemo(() => (currentDsl ? validateGraph(currentDsl) : { ok: true, errors: [], warnings: [] }), [currentDsl]);

  const save = async () => {
    if (!currentDsl) return;
    setSaving(true);
    setSaveMsg(null);
    setConflict(false);
    try {
      const res = await saveFlowDsl(id, flowId, project.version, currentDsl, getApiToken);
      setProject((p) => ({ ...p, version: res.project.version }));
      setDirty(false);
      setSaveMsg({ type: 'ok', text: `Saved (Design Model v${res.designModelVersion}).` });
    } catch (err) {
      if (err.reason === 'stale-version') {
        // Not a dead-end (P0.5): the flow-level PATCH replaces this ONE flow's DSL wholesale, so
        // recovery = re-read the fresh project version and re-submit the LOCAL graph.
        setConflict(true);
        setSaveMsg({ type: 'err', text: 'Project changed elsewhere — your edits are intact; use "Reload & retry save".' });
      } else if (err.reason === 'locked') setSaveMsg({ type: 'err', text: 'A generation job is running — editing is locked.' });
      else setSaveMsg({ type: 'err', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Stale-version recovery: fetch the fresh project version, then retry the save keeping the local
  // graph. (If someone else edited THIS flow concurrently, our save intentionally wins — the
  // engineer chose to keep their local edits.)
  const retryStaleSave = async () => {
    if (!currentDsl) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const proj = await getProject(id, getApiToken);
      const res = await saveFlowDsl(id, flowId, proj.version, currentDsl, getApiToken);
      setProject({ ...proj, version: res.project.version });
      setDirty(false);
      setConflict(false);
      setSaveMsg({ type: 'ok', text: `Saved (Design Model v${res.designModelVersion}).` });
    } catch (err) {
      if (err.reason === 'locked') setSaveMsg({ type: 'err', text: 'A generation job is running — editing is locked.' });
      else setSaveMsg({ type: 'err', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Node label lookup for the edge panel (source/target names).
  const nodeLabelById = useMemo(() => {
    const m = new Map();
    for (const n of nodes) m.set(n.id, n.data.label || n.id);
    return m;
  }, [nodes]);

  if (loading) return <Center py="xl"><Loader color="orange" /></Center>;
  if (error) {
    return (
      <Box p="md">
        <Button variant="subtle" color="orange" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(`../${id}`)}>Back</Button>
        <Alert color="red" icon={<IconAlertCircle size={16} />} mt="md">{error}</Alert>
      </Box>
    );
  }

  return (
    // Full-bleed: the editor is mounted inside AwsApp's centered `<Container size="xl">` (max-width +
    // 20px padding), which would squeeze the canvas into a narrow centered column. Break out to the
    // full viewport width so the diagram uses all available horizontal space (no wasted side margins,
    // less horizontal scrolling). The `left: 50% / margin-left: -50vw` pair is the standard full-bleed
    // technique against a centered parent; the negative top margin cancels the Container's 20px pad.
    <Box style={{
      display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)',
      width: '100vw', position: 'relative', left: '50%', marginLeft: '-50vw', marginTop: -20,
    }}>
      <Group justify="space-between" p="xs" style={{ borderBottom: '1px solid #e9ecef' }}>
        <Group gap="xs">
          <Button variant="subtle" color="orange" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={requestBack}>Back</Button>
          <Title order={5}>{flowMeta?.name}</Title>
          {isModuleFlowType(flowMeta?.flowType) ? (
            <Badge variant="light" color="grape">Flow Module</Badge>
          ) : (
            <Select
              size="xs"
              w={190}
              aria-label="Flow type"
              data={FLOW_TYPE_SELECT_DATA}
              value={flowMeta?.flowType || 'CONTACT_FLOW'}
              onChange={setFlowType}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
            />
          )}
          {dirty && <Badge color="yellow" variant="light">unsaved</Badge>}
          {!validation.ok && <Badge color="red" variant="light" leftSection={<IconAlertCircle size={12} />}>{validation.errors.length} error(s)</Badge>}
          {validation.ok && validation.warnings.length > 0 && (
            <Badge color="yellow" variant="light" leftSection={<IconAlertTriangle size={12} />}>{validation.warnings.length} warning(s)</Badge>
          )}
        </Group>
        <Group gap="xs">
          {saveMsg && <Text size="xs" c={saveMsg.type === 'ok' ? 'green' : saveMsg.type === 'info' ? 'blue' : 'red'}>{saveMsg.text}</Text>}
          {conflict && (
            <Button size="xs" color="yellow" variant="light" loading={saving} onClick={retryStaleSave}>
              Reload &amp; retry save
            </Button>
          )}
          <Button size="xs" variant="light" color="gray" leftSection={<IconLayoutGrid size={14} />} disabled={nodes.length === 0} onClick={tidyLayout}>Tidy layout</Button>
          <Menu>
            <Menu.Target><Button size="xs" variant="light" color="orange" leftSection={<IconPlus size={14} />}>Add block</Button></Menu.Target>
            <Menu.Dropdown>
              {paletteFor(flowMeta?.flowType).map((grp) => (
                <React.Fragment key={grp.group}>
                  <Menu.Label>{grp.group}</Menu.Label>
                  {grp.items.map((it) => <Menu.Item key={it.type} onClick={() => addNode(it.type, it.label)}>{it.label}</Menu.Item>)}
                </React.Fragment>
              ))}
            </Menu.Dropdown>
          </Menu>
          <Button size="xs" color="orange" leftSection={<IconDeviceFloppy size={14} />} loading={saving} disabled={!dirty} onClick={save}>Save</Button>
        </Group>
      </Group>

      <Box style={{ flex: 1, display: 'flex' }}>
        <Box style={{ flex: 1 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={(c) => { onNodesChange(c); if (c.some((x) => x.type === 'position' && x.dragging === false)) markDirty(); }}
            onEdgesChange={(c) => { onEdgesChange(c); markDirty(); }}
            onConnect={onConnect}
            onReconnect={onReconnect}
            onNodeClick={(_, n) => { setSelectedEdge(null); setSelected(n); }}
            onEdgeClick={(_, e) => { setSelected(null); setSelectedEdge(e); }}
            onPaneClick={() => { setSelected(null); setSelectedEdge(null); }}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </Box>

        <Box style={{ width: 320, borderLeft: '1px solid #e9ecef', overflow: 'hidden' }}>
          <ScrollArea h="100%" p="sm">
            {selectedEdge ? (
              <EdgePanel
                edge={selectedEdge}
                fromLabel={nodeLabelById.get(selectedEdge.source)}
                toLabel={nodeLabelById.get(selectedEdge.target)}
                onChange={updateSelectedEdge}
                onDelete={deleteSelectedEdge}
              />
            ) : selected ? (
              <NodePanel node={selected} onChange={updateSelectedNode} onDelete={deleteSelected} />
            ) : (
              <Stack gap="xs">
                <Title order={6}>Flow checks</Title>
                {validation.errors.map((e, i) => <Alert key={`e${i}`} color="red" p="xs" icon={<IconAlertCircle size={14} />}>{e}</Alert>)}
                {validation.warnings.map((w, i) => <Alert key={`w${i}`} color="yellow" p="xs" icon={<IconAlertTriangle size={14} />}>{w}</Alert>)}
                {validation.ok && validation.warnings.length === 0 && <Text size="sm" c="green">No issues — import-ready.</Text>}
                <Divider my="xs" />
                <Text size="xs" c="dimmed">
                  Select a node to edit its properties, or a line to retype/delete it. Each block exit has
                  its own coloured connector — drag from one to wire that branch.
                </Text>
              </Stack>
            )}
          </ScrollArea>
        </Box>
      </Box>

      {/* Back pressed with unsaved edits — confirm before discarding (edits are NOT autosaved). */}
      <Modal opened={leaveConfirm} onClose={() => setLeaveConfirm(false)} title="Unsaved edits" centered>
        <Stack>
          <Text size="sm">
            This flow has unsaved edits — leaving discards them. Save first, or leave anyway?
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" onClick={() => setLeaveConfirm(false)}>Stay</Button>
            <Button color="red" variant="light" onClick={() => { setLeaveConfirm(false); goBack(); }}>
              Discard &amp; leave
            </Button>
            <Button
              color="orange"
              loading={saving}
              onClick={async () => { await save(); setLeaveConfirm(false); }}
            >
              Save first
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

const EDGE_TYPE_DATA = [
  { value: 'default', label: 'Success / default' },
  { value: 'condition', label: 'Condition' },
  { value: 'error', label: 'Error' },
];
const CONDITION_OPERATORS = ['Equals', 'NumberLessThan', 'NumberGreaterThan', 'NumberEqualTo', 'NumberGreaterOrEqualTo', 'NumberLessOrEqualTo'];
const COMMON_ERROR_TYPES = ['NoMatchingError', 'NoMatchingCondition', 'InputTimeLimitExceeded', 'InvalidPhoneNumber', 'InvalidCallbackNumber'];

// Right-panel editor for a selected line: retype the branch (success/condition/error), set its
// condition value or error type, reroute info, and delete. Mirrors the NodePanel pattern.
function EdgePanel({ edge, fromLabel, toLabel, onChange, onDelete }) {
  const data = edge.data || {};
  const kind = edgeColorKind(data); // success | condition | error
  const uiType = kind === 'success' ? 'default' : kind;
  const cond = data.condition || {};
  const color = (EDGE_KIND_STYLE[kind] || EDGE_KIND_STYLE.success).color;

  const setType = (t) => {
    if (t === 'default') onChange({ kind: 'default', condition: undefined, errorType: undefined });
    else if (t === 'condition') onChange({ kind: 'condition', errorType: undefined, condition: data.condition || { Operator: 'Equals', Operands: [''] } });
    else onChange({ kind: 'error', condition: undefined, errorType: data.errorType || 'NoMatchingError' });
  };
  const setOperator = (op) => onChange({ condition: { Operator: op || 'Equals', Operands: cond.Operands || [''] } });
  const setOperand = (v) => onChange({ condition: { Operator: cond.Operator || 'Equals', Operands: [v] } });
  const setErrorType = (v) => onChange({ errorType: v });

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Group gap={6}>
          <IconArrowsSplit2 size={16} color={color} />
          <Title order={6}>Branch</Title>
        </Group>
        <ActionIcon color="red" variant="subtle" onClick={onDelete} aria-label="Delete line"><IconTrash size={16} /></ActionIcon>
      </Group>
      <Text size="xs" c="dimmed">
        <b>{fromLabel || edge.source}</b> → <b>{toLabel || edge.target}</b>
      </Text>
      <Badge variant="light" style={{ color, borderColor: color }}>{edgeLabel(data) || 'Success'}</Badge>

      <Select label="Branch type" size="xs" data={EDGE_TYPE_DATA} value={uiType} onChange={setType} allowDeselect={false} comboboxProps={{ withinPortal: true }} />

      {uiType === 'condition' && (
        <>
          <Select label="Operator" size="xs" data={CONDITION_OPERATORS} value={cond.Operator || 'Equals'} onChange={setOperator} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
          <TextInput
            label="Condition value"
            size="xs"
            placeholder="e.g. 1 (DTMF digit) or AGENCY"
            value={(cond.Operands && cond.Operands[0]) || ''}
            onChange={(e) => setOperand(e.currentTarget.value)}
          />
        </>
      )}
      {uiType === 'error' && (
        <Select
          label="Error type"
          size="xs"
          data={COMMON_ERROR_TYPES}
          value={data.errorType || 'NoMatchingError'}
          onChange={setErrorType}
          searchable
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
      )}

      <Text size="xs" c="dimmed">
        Drag the line's endpoint to reroute it. Click <b>Save</b> (top bar) to persist your edits —
        they are not saved automatically.
      </Text>
    </Stack>
  );
}

function NodePanel({ node, onChange, onDelete }) {
  const { type, props } = node.data;
  const setProp = (k, v) => onChange({ props: { ...props, [k]: v } });
  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Title order={6}>{node.data.label}</Title>
        <ActionIcon color="red" variant="subtle" onClick={onDelete}><IconTrash size={16} /></ActionIcon>
      </Group>
      <Badge variant="light" color="gray">{type}{branches(type) ? ' · branches' : isTerminal(type) ? ' · terminal' : ''}</Badge>
      <TextInput label="Label" value={node.data.label} onChange={(e) => onChange({ label: e.currentTarget.value })} />

      {['MessageParticipant', 'GetParticipantInput', 'ConnectParticipantWithLexBot'].includes(type) && (
        <Textarea label="Prompt text" autosize minRows={2} value={props.prompt || ''} onChange={(e) => setProp('prompt', e.currentTarget.value)} />
      )}
      {type === 'InvokeLambdaFunction' && (
        <TextInput label="Lambda ref / ARN" value={props.lambdaRef || ''} onChange={(e) => setProp('lambdaRef', e.currentTarget.value)} />
      )}
      {['UpdateContactTargetQueue', 'TransferContactToQueue'].includes(type) && (
        <TextInput label="Queue ref" value={props.queueRef || ''} onChange={(e) => setProp('queueRef', e.currentTarget.value)} />
      )}
      {type === 'Compare' && (
        <TextInput label="Comparison value" value={props.compareValue || ''} onChange={(e) => setProp('compareValue', e.currentTarget.value)} />
      )}
      {type === 'InvokeFlowModule' && (
        <TextInput label="Module ref" value={props.moduleRef || ''} onChange={(e) => setProp('moduleRef', e.currentTarget.value)} />
      )}
      {type === 'TransferToFlow' && (
        <TextInput label="Flow ref" value={props.flowRef || ''} onChange={(e) => setProp('flowRef', e.currentTarget.value)} />
      )}

      <ParametersView parameters={node.data.parameters} />

      <Text size="xs" c="dimmed">
        Click <b>Save</b> (top bar) to persist your edits into the Design Model (a new version, under
        the concurrency lock) — they are not saved automatically.
      </Text>
    </Stack>
  );
}

// Read-only view of the node's raw Connect `parameters` (the authoritative source). Surfaces detail
// for generated flows whose params aren't covered by the friendly inputs above (e.g. the Attributes
// map of UpdateContactAttributes), so a selected node never shows an empty panel.
function ParametersView({ parameters }) {
  const params = parameters || {};
  const keys = Object.keys(params);
  if (!keys.length) return null;
  const render = (v) => {
    if (v == null) return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  return (
    <>
      <Divider my="xs" label="Parameters" labelPosition="left" />
      <Stack gap={4}>
        {keys.map((k) => (
          <Group key={k} gap={6} wrap="nowrap" align="flex-start">
            <Text size="xs" fw={600} c="dimmed" style={{ flexShrink: 0 }}>{k}</Text>
            <Text size="xs" c="dark" style={{ wordBreak: 'break-word' }}>{render(params[k])}</Text>
          </Group>
        ))}
      </Stack>
    </>
  );
}

export default function DiagramEditor() {
  return (
    <ReactFlowProvider>
      <DiagramEditorInner />
    </ReactFlowProvider>
  );
}
